import {
  FormControl,
  FormControlLabel,
  FormLabel,
  GKButton,
  GKDatePicker,
  GKRadio,
  GKSearchSelect,
  GKTextField,
  Grid2,
  RadioGroup,
  ReloadIcon,
  ResetIcon,
  Show,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "components";
import dayjs from "dayjs";
import DashboardLayout from "layouts/DashboardLayout";
import { map, sum } from "lodash";
import { useEffect, useMemo } from "react";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  useCreateCollectionMutation,
  useFetchClientInvoicesMutation,
  useFetchCollectionInvoicesByIdQuery,
  useUpdateCollectionMutation,
} from "store/features/Revenue/ProformaApi";
import { useAppSelector } from "store/store";
import { UserRoleTypes } from "utils/common";
import { matchDate } from "utils/helpers";
import { moneyCommaSeparator, numberToCurrency } from "utils/MathFunction";
import useDocumentTitle from "utils/useDocumentTitle";
import { Cell, TableBodyCell } from "../utils";

const paymentType = [
  { name: "Cash", value: "Cash" },
  { name: "E-mandate ", value: "E Mandates" },
  { name: "Bank", value: "Bank" },
];

const FeeCollectionForm = () => {
  useDocumentTitle("Collection");

  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const userData = useAppSelector((state) => state?.user?.userData);
  const clientList = useAppSelector((state) => state?.app?.clientList);
  const clientId = new URLSearchParams(location.search).get("client_id");

  const { data, isFetching } = useFetchCollectionInvoicesByIdQuery(
    id as string,
    { skip: !id },
  );

  const findClient = (cid: string | number | null) =>
    cid ? clientList.find((c) => +c.id === +cid) : undefined;

  const form = useForm({
    defaultValues: {
      client: clientId
        ? {
            ...findClient(clientId),
            name: findClient(clientId)?.name ?? " ",
            value: findClient(clientId)?.id,
          }
        : "",
      amount: "",
      paymentDate: "",
      status: "",
      outStandingAmount: "",
      paymentType: "",
      refNumber: "",
      invoicePaymentHistory: [],
      totalAmount: "",
    },
    values: id
      ? {
          ...data?.data,
          client: {
            ...findClient(data?.data?.client),
            name: findClient(data?.data?.client)?.name ?? " ",
            value: findClient(data?.data?.client)?.id,
          },
        }
      : undefined,
  });

  const { fields } = useFieldArray({
    control: form.control,
    name: "invoicePaymentHistory",
  });

  const [watchedAmount, watchedInvoiceHistory] = useWatch({
    control: form.control,
    name: ["amount", "invoicePaymentHistory"],
  }) as [string, any[]];

  const { totalAdjustedAmount, totalUnadjustedAmount } = useMemo(() => {
    const adjusted = sum(
      watchedInvoiceHistory?.map((i) => +i?.paidAmount || 0) ?? [],
    );
    const total = parseFloat(watchedAmount || "0");
    return {
      totalAdjustedAmount: adjusted,
      totalUnadjustedAmount: total - adjusted,
    };
  }, [watchedInvoiceHistory, watchedAmount]);

  useEffect(() => {
    if (watchedInvoiceHistory?.length) {
      form.setValue("adjustedAmount", String(totalAdjustedAmount));
      form.setValue("outStandingAmount", String(totalUnadjustedAmount));
      form.setValue("totalAmount", String(totalAdjustedAmount));
    }
  }, [totalAdjustedAmount, totalUnadjustedAmount, watchedInvoiceHistory]);

  const [createCollection, { isLoading: createCollectionLoading }] =
    useCreateCollectionMutation();
  const [updateCollection, { isLoading: updateCollectionLoading }] =
    useUpdateCollectionMutation();
  const [fetchClientInvoices, { isLoading }] = useFetchClientInvoicesMutation();

  const onSubmit = async (values: any) => {
    try {
      const payload = {
        ...values,
        client: values.client?.id,
        status: "Partially Paid",
      };
      id
        ? await updateCollection({ id, data: payload }).unwrap()
        : await createCollection(payload).unwrap();
      navigate(-1);
    } catch (err) {
      console.error("Create/Update collection failed:", err);
    }
  };

  const handleFetchUnpaidInvoices = form.handleSubmit(async (values) => {
    try {
      const res = await fetchClientInvoices(values?.client?.id).unwrap();
      form.setValue("invoicePaymentHistory", res?.data);
    } catch (err) {
      console.error("Fetch invoices failed:", err);
    }
  });

  const summaryItems = [
    { name: "Total Unadjusted Amount", value: totalUnadjustedAmount },
    { name: "Total Adjusted Amount", value: totalAdjustedAmount },
  ];

  return (
    <DashboardLayout
      title={"Add Fee Collection"}
      loading={isFetching}
      disablePadding
    >
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Grid2 container>
          <Grid2
            size={id ? 3 : 4}
            borderRight={`1px solid var(--neutral-100)`}
            p={3}
            height={"calc(100vh - 66px)"}
            overflow={"auto"}
            alignContent={"flex-start"}
            bgcolor={id ? "var(--neutral-bg-100)" : undefined}
          >
            <Grid2 container size={12} spacing={4}>
              <Grid2 size={12}>
                <Typography
                  fontSize={12}
                  fontWeight={600}
                  color="var(--neutral-500)"
                >
                  Fee Collection Details
                </Typography>
              </Grid2>
              <Show.When isTrue={!id}>
                <Grid2 size={12} mt={1}>
                  <Controller
                    name="client"
                    control={form.control}
                    rules={{
                      required: {
                        value: true,
                        message: "Please select client",
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <GKSearchSelect
                        {...field}
                        requiredField
                        readOnly={!!id || !!clientId}
                        disableClearable={false}
                        inputLabel="Client"
                        options={clientList || []}
                        onChange={(e, val) => field.onChange(val)}
                        getOptionLabel={(option) => option.name || option}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={12}>
                  <Controller
                    name="paymentType"
                    control={form.control}
                    render={({ field }) => (
                      <FormControl>
                        <FormLabel
                          sx={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: "var(--black)",
                          }}
                        >
                          Payment Type
                        </FormLabel>
                        <RadioGroup
                          {...field}
                          row
                          onChange={(e) => field.onChange(e.target.value)}
                        >
                          {map(paymentType, (item) => {
                            return (
                              <FormControlLabel
                                key={item?.name}
                                label={item?.name}
                                value={item.value}
                                checked={field.value === item.value}
                                control={
                                  <GKRadio
                                    disableLeftPadding
                                    disabled={Boolean(id)}
                                  />
                                }
                              />
                            );
                          })}
                        </RadioGroup>
                      </FormControl>
                    )}
                  />
                </Grid2>
                <Grid2 size={12}>
                  <Controller
                    name={"paymentDate"}
                    control={form.control}
                    rules={{
                      required: {
                        value: true,
                        message: "Please select payment date",
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <GKDatePicker
                        {...field}
                        inputLabel="Fee Collection Date"
                        readOnly={Boolean(id)}
                        value={dayjs(field.value)}
                        onChange={(value) => {
                          field.onChange(dayjs(value).format("YYYY-MM-DD"));
                        }}
                        disableFuture
                        maxDate={dayjs()}
                        requiredField
                        slotProps={{
                          textField: {
                            error: !!error,
                            helperText: error?.message,
                          },
                        }}
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={12}>
                  <Controller
                    name={`amount`}
                    control={form.control}
                    rules={{
                      required: {
                        value: true,
                        message: "Please enter amount",
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <GKTextField
                        {...field}
                        requiredField
                        inputLabel={"Amount"}
                        type="number"
                        slotProps={{ htmlInput: { readOnly: Boolean(id) } }}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={12}>
                  <Controller
                    name={"refNumber"}
                    control={form.control}
                    render={({ field }) => (
                      <GKTextField
                        {...field}
                        inputLabel={"Reference No."}
                        slotProps={{ htmlInput: { readOnly: Boolean(id) } }}
                      />
                    )}
                  />
                </Grid2>

                <Grid2
                  size={12}
                  mt={1}
                  display={"flex"}
                  alignItems={"center"}
                  gap={1}
                >
                  <GKButton
                    variant="outlined"
                    fullWidth
                    color="dark"
                    onClick={() =>
                      form.reset({
                        ...form.formState.defaultValues,
                        client: clientId
                          ? {
                              ...findClient(clientId),
                              name: findClient(clientId)?.name ?? " ",
                              value: findClient(clientId)?.id,
                            }
                          : "",
                      })
                    }
                    startIcon={<ResetIcon />}
                  >
                    Reset all
                  </GKButton>
                  <GKButton
                    variant="contained"
                    fullWidth
                    loading={isLoading}
                    onClick={handleFetchUnpaidInvoices}
                    startIcon={<ReloadIcon />}
                  >
                    Fetch Unpaid Invoices
                  </GKButton>
                </Grid2>
              </Show.When>
              <Show.When isTrue={!!id}>
                <Grid2 size={12} mt={1}>
                  <Typography
                    fontSize={12}
                    fontWeight={500}
                    color="var(--grey-700)"
                  >
                    Client Name:
                  </Typography>
                  <Typography
                    fontSize={16}
                    fontWeight={500}
                    color="var(--grey-900)"
                  >
                    {findClient(data?.data?.client)?.name}
                  </Typography>
                </Grid2>
                <Grid2 size={12}>
                  <Typography
                    fontSize={12}
                    fontWeight={500}
                    color="var(--grey-700)"
                  >
                    Payment Type:
                  </Typography>
                  <Typography
                    fontSize={16}
                    fontWeight={500}
                    color="var(--grey-900)"
                  >
                    {data?.data?.paymentType || "-"}
                  </Typography>
                </Grid2>
                <Grid2 size={12}>
                  <Typography
                    fontSize={12}
                    fontWeight={500}
                    color="var(--grey-700)"
                  >
                    Fee Collection Date:
                  </Typography>
                  <Typography
                    fontSize={16}
                    fontWeight={500}
                    color="var(--grey-900)"
                  >
                    {matchDate(data?.data?.paymentDate, "DD/MM/YYYY") || "-"}
                  </Typography>
                </Grid2>
                <Grid2 size={12}>
                  <Typography
                    fontSize={12}
                    fontWeight={500}
                    color="var(--grey-700)"
                  >
                    Amount:
                  </Typography>
                  <Typography
                    fontSize={16}
                    fontWeight={500}
                    color="var(--grey-900)"
                  >
                    {numberToCurrency(data?.data?.amount)}
                  </Typography>
                </Grid2>
                <Grid2 size={12}>
                  <Typography
                    fontSize={12}
                    fontWeight={500}
                    color="var(--grey-700)"
                  >
                    Invoice Reference No:
                  </Typography>
                  <Typography
                    fontSize={16}
                    fontWeight={500}
                    color="var(--grey-900)"
                  >
                    {data?.data?.refNumber || "-"}
                  </Typography>
                </Grid2>
              </Show.When>
            </Grid2>
          </Grid2>

          <Grid2 size={id ? 9 : 8}>
            <Grid2
              container
              size={12}
              p={3}
              spacing={3}
              borderBottom={`1px solid var(--neutral-100)`}
            >
              <Grid2 size={12}>
                <Typography
                  fontSize={12}
                  fontWeight={600}
                  color="var(--neutral-500)"
                  textTransform={"uppercase"}
                >
                  Summary details
                </Typography>
              </Grid2>

              <Grid2 container size={12} display={"flex"} spacing={"12px"}>
                {summaryItems.map((item, key) => {
                  return (
                    <Grid2 key={key} size={6}>
                      <Grid2
                        container
                        spacing={"12px"}
                        p={2}
                        borderRadius={1}
                        bgcolor={"var(--grey-bg-100)"}
                      >
                        <Grid2 size={12}>
                          <Typography
                            fontSize={13}
                            fontWeight={500}
                            color="var(--grey-700)"
                          >
                            {item?.name}
                          </Typography>
                        </Grid2>
                        <Grid2 size={12}>
                          <Typography
                            fontSize={18}
                            fontWeight={500}
                            display={"flex"}
                            alignItems={"center"}
                            gap={0.5}
                            color={
                              key === 0
                                ? totalAdjustedAmount >
                                  +form.getValues("amount")
                                  ? "error"
                                  : "var(--success-700)"
                                : "var(--black)"
                            }
                          >
                            {numberToCurrency(item?.value)}
                          </Typography>
                        </Grid2>
                      </Grid2>
                    </Grid2>
                  );
                })}
              </Grid2>
            </Grid2>

            <Grid2
              container
              size={12}
              spacing={2}
              p={3}
              maxHeight={"calc(100vh - 320px)"}
              minHeight={"calc(100vh - 320px)"}
              overflow={"auto"}
              alignContent={"flex-start"}
            >
              {form.getValues("invoicePaymentHistory")?.length > 0 && (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow
                        sx={{
                          "&.MuiTableRow-head th": { p: "12px" },
                        }}
                      >
                        <Cell align="center">#</Cell>
                        <Cell>Date</Cell>
                        <Cell>invoice no.</Cell>
                        <Cell align="right">Amount (₹)</Cell>
                        <Cell>Adjusted Amount (₹)</Cell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {fields.map((row: any, index: number) => {
                        return (
                          <TableRow
                            key={row.id}
                            sx={{ "&.MuiTableRow-head th": { p: "12px" } }}
                          >
                            <TableBodyCell
                              align="center"
                              style={{ width: "10%" }}
                            >
                              {index + 1}
                            </TableBodyCell>
                            <TableBodyCell style={{ width: "20%" }}>
                              {dayjs(row.createdAt).format("DD-MM-YYYY")}
                            </TableBodyCell>
                            <TableBodyCell style={{ width: "20%" }}>
                              {row.invoiceNumber}
                            </TableBodyCell>
                            <TableBodyCell
                              align="right"
                              style={{ width: "20%" }}
                            >
                              {moneyCommaSeparator(+row.totalFee)}
                            </TableBodyCell>
                            <TableBodyCell style={{ width: "10%" }}>
                              <Controller
                                name={`invoicePaymentHistory.${index}.paidAmount`}
                                control={form.control}
                                defaultValue={0}
                                render={({ field }) => (
                                  <GKTextField
                                    {...field}
                                    size="small"
                                    onFocus={(e) => {
                                      if (!e.target.value) {
                                        form.setValue(
                                          `invoicePaymentHistory.${index}.paidAmount`,
                                          row?.remainingAmount?.toFixed(2),
                                        );
                                      }
                                    }}
                                    onChange={(e) => {
                                      field.onChange(e);
                                      form.trigger(
                                        `invoicePaymentHistory.${index}.paidAmount`,
                                      );
                                    }}
                                    type="number"
                                    slotProps={{
                                      htmlInput: {
                                        readOnly:
                                          Boolean(id) &&
                                          row?.isPayment === "completed",
                                      },
                                    }}
                                  />
                                )}
                              />
                            </TableBodyCell>
                          </TableRow>
                        );
                      })}
                      <TableRow>
                        <TableBodyCell colSpan={3} />
                        <TableBodyCell>Total Amount</TableBodyCell>
                        <TableBodyCell
                          style={{ width: "40%", padding: "22px 12px" }}
                        >
                          <Controller
                            name={`totalAmount`}
                            control={form.control}
                            render={() => (
                              <Typography
                                fontSize={14}
                                fontWeight={600}
                                color={
                                  totalAdjustedAmount >
                                  +form.getValues("amount")
                                    ? "error"
                                    : "var(--black)"
                                }
                              >
                                {numberToCurrency(totalAdjustedAmount)}
                              </Typography>
                            )}
                          />
                        </TableBodyCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Grid2>

            <Show.When isTrue={userData?.userRole === UserRoleTypes.advisor}>
              {fields.length !== 0 && (
                <Grid2
                  size={12}
                  display={"flex"}
                  gap={1}
                  justifyContent={"flex-end"}
                  px={3}
                  py={2}
                >
                  <GKButton
                    variant="contained"
                    type="submit"
                    loading={createCollectionLoading || updateCollectionLoading}
                    disabled={
                      +form.getValues("totalAmount") > +form.getValues("amount")
                    }
                  >
                    {id ? "Update" : "Save"}
                  </GKButton>
                </Grid2>
              )}
            </Show.When>
          </Grid2>
        </Grid2>
      </form>
    </DashboardLayout>
  );
};

export default FeeCollectionForm;
